/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */
import * as moment from 'moment-timezone';

export class TimezoneHelper {
  static getTimeZoneDisplayList(): TimeZoneDisplay[] {
    const timeZoneList = TimezoneHelper.getTimeZoneList();
    const timeZones: { title: string; value: string }[] = [];

    for (let i = 0; i < timeZoneList.length; i++) {
      timeZones.push({
        title: `GMT ${timeZoneList[i].offset}:00 ${timeZoneList[i].location}`,
        value: `GMT${timeZoneList[i].offset}00`,
      });
    }

    return timeZones;
  }

  static getMapFormattedDisplayTimezoneList(): TimeZoneMap {
    const timezoneList = this.getTimeZoneList();
    const formattedMapTimezone: TimeZoneMap = {};

    timezoneList.forEach((item) => {
      const timezoneKey = 'GMT' + item.offset + '00';
      formattedMapTimezone[timezoneKey] = 'GMT ' + item.offset + ':00 ' + item.location;
    });

    return formattedMapTimezone;
  }

  private static getTimeZoneList(): TimeZone[] {
    const popularTimeZones: TimeZone[] = this.getPopularLocations().map((location) => {
      const id = location.location.replace(/ /g, '_');
      const utcCode = moment.tz(id).format('Z');
      const offset = utcCode.slice(0, utcCode.indexOf(':'));
      return { location: id, offset: offset };
    });

    const possibleOffsetNumbers = [
      '-11',
      '-10',
      '-09',
      '-08',
      '-07',
      '-06',
      '-05',
      '-04',
      '-03',
      '-02',
      '-01',
      '+00',
      '+01',
      '+02',
      '+03',
      '+04',
      '+05',
      '+06',
      '+07',
      '+08',
      '+09',
      '+10',
      '+11',
      '+12',
    ];

    const existOffsets = new Set(popularTimeZones.map((z) => z.offset));
    const missingOffsets = possibleOffsetNumbers.filter((o) => !existOffsets.has(o));
    const timeZoneNames = moment.tz.names();

    const filled = missingOffsets.map((off) => {
      const locationName = timeZoneNames.find((n) => moment.tz(n).format('Z').slice(0, 3) === off);

      const location = locationName ?? off;

      return {
        offset: off,
        location: location,
      };
    });

    return [...popularTimeZones, ...filled].sort((a, b) => Number(a.offset) - Number(b.offset));
  }

  private static getPopularLocations(): Location[] {
    return [
      { location: 'Pacific/Auckland' },
      { location: 'Pacific/Guadalcanal' },
      { location: 'Australia/Sydney' },
      { location: 'Asia/Tokyo' },
      { location: 'Asia/Shanghai' },
      { location: 'Asia/Bangkok' },
      { location: 'Asia/Dhaka' },
      { location: 'Asia/Karachi' },
      { location: 'Asia/Dubai' },
      { location: 'Asia/Riyadh' },
      { location: 'Africa/Johannesburg' },
      { location: 'Europe/Berlin' },
      { location: 'Europe/London' },
      { location: 'Atlantic/Cape Verde' },
      { location: 'America/Sao Paulo' },
      { location: 'America/Caracas' },
      { location: 'America/New York' },
      { location: 'America/Mexico City' },
      { location: 'America/Denver' },
      { location: 'America/Los Angeles' },
      { location: 'America/Anchorage' },
      { location: 'Pacific/Honolulu' },
      { location: 'Pacific/Pago Pago' },
    ];
  }
}

export interface Location {
  location: string;
}

export interface TimeZone {
  offset: string;
  location: string;
}

export interface TimeZoneDisplay {
  title: string;
  value: string;
}

export interface TimeZoneMap {
  [key: string]: string;
}
